import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FormProvider, useForm } from "react-hook-form";
import MedicationIcon from "@mui/icons-material/Medication";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import baseUrl from "../../../api/baseUrl";
import FormDate from "../../../components/formFeild/FormDate";
import BottomTab from "../../../components/pagination/BottomTab";
import CommonLayout from "../../../layouts/commonLayout/CommonLayout";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../redux/loader/loaderActions";
import { RootState } from "../../../redux/store";

interface FormData {
  startDate: string;
  endDate: string;
}

interface PrescriptionItem {
  _id: string;
  drugs?: string;
  type?: string;
  dose?: string;
  frequency?: string;
  duration?: string;
}

interface InvoicePrescriptionGroup {
  treatmentId: string;
  invoiceNo?: string;
  visitDate?: string;
  patientName?: string;
  regNo?: string;
  mobileNo?: string;
  doctorName?: string;
  prescriptions: PrescriptionItem[];
}

type DentistOption = {
  _id: string;
  title?: string;
  lastName?: string;
};

function dentistLabel(d: DentistOption): string {
  return [d.title, d.lastName].filter(Boolean).join(" ").trim() || "Dentist";
}

function formatDate(value?: string): string {
  if (!value) return "—";
  return value.split("T")[0];
}

function PrescriptionLIst() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const search = useSelector((state: RootState) => state.searching.patientData);
  const methods = useForm<FormData>();
  const { watch, reset } = methods;
  const startDate = watch("startDate");
  const endDate = watch("endDate");

  const [groups, setGroups] = useState<InvoicePrescriptionGroup[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDoctorId, setFilterDoctorId] = useState("all");
  const [dentists, setDentists] = useState<DentistOption[]>([]);

  useEffect(() => {
    baseUrl
      .get("/appointment/findAllAppointmentDentist")
      .then((response) => {
        const list = Array.isArray(response.data?.data) ? response.data.data : [];
        setDentists(
          list
            .map((d: DentistOption) => ({
              _id: String(d?._id ?? ""),
              title: d?.title,
              lastName: d?.lastName,
            }))
            .filter((d: DentistOption) => d._id)
        );
      })
      .catch(() => setDentists([]));
  }, []);

  const getData = useCallback(
    (
      keyword: string,
      pageNum: number,
      from?: string,
      to?: string,
      doctorId?: string
    ) => {
      dispatch(setloadeActive());
      baseUrl
        .get("treatment/getAllPrescriptionList", {
          headers: {
            keyword,
            page: pageNum,
          },
          params: {
            startDate: from || undefined,
            endDate: to || undefined,
            doctorId:
              doctorId && doctorId !== "all" ? doctorId : undefined,
          },
        })
        .then((response) => {
          dispatch(setloadeDeactive());
          setGroups(response.data.data ?? []);
          setCount(response.data.count ?? 0);
        })
        .catch(() => {
          dispatch(setloadeDeactive());
          setGroups([]);
          setCount(0);
        });
    },
    [dispatch]
  );

  useEffect(() => {
    const activeSearchTerm = searchTerm || search || "";
    const from = startDate && endDate ? startDate : undefined;
    const to = startDate && endDate ? endDate : undefined;
    getData(activeSearchTerm, page, from, to, filterDoctorId);
  }, [search, searchTerm, page, filterDoctorId, startDate, endDate, getData]);

  const onPageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  }, []);

  const handleDoctorFilter = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setFilterDoctorId(e.target.value);
      setPage(1);
    },
    []
  );

  const handleReset = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      reset();
      setSearchTerm("");
      setFilterDoctorId("all");
      setPage(1);
    },
    [reset]
  );

  const openPrescription = useCallback(
    (treatmentId?: string) => {
      if (treatmentId) {
        navigate(`/client/treatment-diagnose/add-treatment/drugs/${treatmentId}`);
      }
    },
    [navigate]
  );

  const fieldLabelClass =
    "text-slate-700 dark:text-slate-300 text-[13px] font-medium leading-[1.4]";
  const cellClass = "px-2.5 py-1.5 text-xs";

  return (
    <CommonLayout>
      <div className="mt-4">
        <div className="rounded-[8px] border border-slate-100 bg-white p-3 shadow-lg shadow-slate-200/50 dark:border-slate-700 dark:bg-slate-800 dark:shadow-slate-900/50">
          <div className="mb-3 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primaryLight">
              <MedicationIcon className="text-white" sx={{ fontSize: 18 }} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                Prescription List
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Prescriptions grouped by invoice number
              </p>
            </div>
          </div>

          <FormProvider {...methods}>
            <form className="flex flex-col gap-2.5 lg:flex-row lg:items-end lg:justify-between">
              <div className="flex flex-1 flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-end">
                <div className="flex w-full flex-col gap-1 sm:max-w-md">
                  <label className={fieldLabelClass}>Search</label>
                  <input
                    type="search"
                    placeholder="Patient, drug, invoice..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none transition-all placeholder:text-slate-400 focus:border-primary focus:ring-1 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:placeholder:text-slate-500"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <div className="flex w-36 shrink-0 flex-col gap-1">
                    <label className={fieldLabelClass}>Doctor</label>
                    <select
                      value={filterDoctorId}
                      onChange={handleDoctorFilter}
                      className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-700 outline-none transition-all focus:border-primary focus:ring-1 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                    >
                      <option value="all">All</option>
                      {dentists.map((d) => (
                        <option key={d._id} value={d._id}>
                          {dentistLabel(d)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <FormDate label="From" name="startDate" sameSize />
                  <FormDate label="To" name="endDate" sameSize />
                </div>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  type="button"
                  onClick={handleReset}
                  className="flex h-8 items-center whitespace-nowrap rounded-lg border border-slate-200 bg-slate-100 px-3 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                >
                  Reset
                </button>
              </div>
            </form>
          </FormProvider>

          {/* Desktop: grouped by invoice */}
          <div className="mt-3 hidden md:block">
            {groups.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 px-3 py-8 text-center text-xs text-slate-500 dark:border-slate-600 dark:text-slate-400">
                <MedicationIcon sx={{ fontSize: 36 }} className="mb-2 opacity-40" />
                <p>No prescriptions found.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {groups.map((group) => (
                  <div
                    key={group.treatmentId}
                    className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-600"
                  >
                    <button
                      type="button"
                      onClick={() => openPrescription(group.treatmentId)}
                      className="flex w-full items-center justify-between gap-3 bg-slate-50/90 px-3 py-2 text-left transition-colors hover:bg-indigo-50/40 dark:bg-slate-700/60 dark:hover:bg-indigo-950/20"
                    >
                      <div className="flex min-w-0 items-center gap-2.5">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 dark:bg-primary/20">
                          <ReceiptLongIcon
                            className="text-primary dark:text-primaryLight"
                            sx={{ fontSize: 16 }}
                          />
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-bold text-indigo-700 dark:text-indigo-300">
                              Invoice #{group.invoiceNo || "—"}
                            </span>
                            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary dark:bg-primary/20 dark:text-primaryLight">
                              {group.prescriptions?.length || 0} drug
                              {(group.prescriptions?.length || 0) !== 1 ? "s" : ""}
                            </span>
                          </div>
                          <p className="truncate text-xs text-slate-600 dark:text-slate-300">
                            {group.patientName || "—"}
                            {group.regNo ? ` · ${group.regNo}` : ""}
                          </p>
                        </div>
                      </div>
                      <div className="shrink-0 text-right text-xs">
                        <p className="font-medium text-slate-700 dark:text-slate-200">
                          {formatDate(group.visitDate)}
                        </p>
                        <p className="text-slate-500 dark:text-slate-400">
                          {group.doctorName || "—"}
                        </p>
                      </div>
                    </button>

                    <table className="w-full">
                      <thead className="border-b border-t border-slate-100 bg-white dark:border-slate-700 dark:bg-slate-800">
                        <tr>
                          <th className={`${cellClass} text-left font-semibold text-slate-500 dark:text-slate-400`}>
                            Drug
                          </th>
                          <th className={`${cellClass} text-left font-semibold text-slate-500 dark:text-slate-400`}>
                            Dose
                          </th>
                          <th className={`${cellClass} text-left font-semibold text-slate-500 dark:text-slate-400`}>
                            Frequency
                          </th>
                          <th className={`${cellClass} text-left font-semibold text-slate-500 dark:text-slate-400`}>
                            Duration
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white dark:divide-slate-700/80 dark:bg-slate-800">
                        {(group.prescriptions || []).map((item) => (
                          <tr
                            key={item._id}
                            onClick={() => openPrescription(group.treatmentId)}
                            className="cursor-pointer transition-colors even:bg-slate-50/40 hover:bg-indigo-50/40 dark:even:bg-slate-800/60 dark:hover:bg-indigo-950/20"
                          >
                            <td className={`${cellClass} font-medium text-slate-800 dark:text-slate-200`}>
                              {item.drugs || "—"}
                            </td>
                            <td className={cellClass}>
                              <span className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary dark:bg-primary/20 dark:text-primaryLight">
                                {item.dose}
                                {item.type}
                              </span>
                            </td>
                            <td className={`${cellClass} text-slate-600 dark:text-slate-300`}>
                              {item.frequency || "—"}
                            </td>
                            <td className={`${cellClass} text-slate-600 dark:text-slate-300`}>
                              {item.duration ? `${item.duration} days` : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Mobile: grouped cards */}
          <div className="mt-3 space-y-3 md:hidden">
            {groups.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-200 px-3 py-5 text-center text-xs text-slate-500 dark:border-slate-600 dark:text-slate-400">
                No prescriptions found.
              </div>
            ) : (
              groups.map((group) => (
                <div
                  key={group.treatmentId}
                  className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-600"
                >
                  <button
                    type="button"
                    onClick={() => openPrescription(group.treatmentId)}
                    className="w-full bg-slate-50 px-2.5 py-2 text-left transition-colors hover:bg-indigo-50/40 dark:bg-slate-700/60 dark:hover:bg-indigo-950/20"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs font-bold text-indigo-700 dark:text-indigo-300">
                          Invoice #{group.invoiceNo || "—"}
                        </p>
                        <p className="text-xs font-semibold text-slate-900 dark:text-slate-100">
                          {group.patientName || "—"}
                        </p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                          {group.doctorName || "—"}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-600 dark:text-slate-300">
                          {formatDate(group.visitDate)}
                        </p>
                        <p className="text-[10px] text-primary dark:text-primaryLight">
                          {group.prescriptions?.length || 0} drugs
                        </p>
                      </div>
                    </div>
                  </button>
                  <div className="divide-y divide-slate-100 dark:divide-slate-700">
                    {(group.prescriptions || []).map((item) => (
                      <div
                        key={item._id}
                        onClick={() => openPrescription(group.treatmentId)}
                        className="cursor-pointer bg-white px-2.5 py-2 transition-colors even:bg-slate-50/40 hover:bg-indigo-50/40 dark:bg-slate-800 dark:even:bg-slate-800/60 dark:hover:bg-indigo-950/20"
                      >
                        <p className="text-xs font-medium text-slate-800 dark:text-slate-200">
                          {item.drugs || "—"}
                        </p>
                        <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-slate-600 dark:text-slate-300">
                          <span className="rounded bg-primary/10 px-2 py-0.5 font-medium text-primary dark:bg-primary/20 dark:text-primaryLight">
                            {item.dose}
                            {item.type}
                          </span>
                          {item.frequency && <span>{item.frequency}</span>}
                          {item.duration && <span>{item.duration} days</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="mt-3 border-t border-slate-100 pt-2.5 dark:border-slate-700">
            <BottomTab title="Invoice" count={count} onPageChange={onPageChange} />
          </div>
        </div>
      </div>
    </CommonLayout>
  );
}

export default PrescriptionLIst;
